import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, MapPin, MessageCircle } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { useAuth } from "@/contexts/AuthContext";
import { SecurePaymentBadge } from "@/components/payment/SecurePaymentBadge";
const quickLinks = [
  { name: "Home", href: "/" },
  { name: "About Us", href: "/about" },
  { name: "Skincare Products", href: "/products" },
  { name: "Gift Cards", href: "/gift-cards" },
  { name: "Customer Testimonials", href: "/testimonials" },
  { name: "Skincare Blog", href: "/blog" },
];

const customerCareLinks = [
  { name: "Contact Us", href: "/contact" },
  { name: "Track Your Order", href: "/track-order" },
  { name: "Skincare Tips & Blog", href: "/blog" },
];

const categoryLinks = [
  { name: "Cleansers", href: "/products?category=Cleanser" },
  { name: "Serums", href: "/products?category=Serum" },
  { name: "Moisturizers", href: "/products?category=Moisturizer" },
  { name: "Sunscreens", href: "/products?category=Sunscreen" },
  { name: "Toners", href: "/products?category=Toner" },
  { name: "Body Care", href: "/products?category=Body" },
  { name: "Face Care", href: "/products?category=Face Care" },
];

const socialLinks = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/nurtureskinstudio_/",
    icon: Instagram,
  },
  {
    name: "Facebook",
    href: "https://web.facebook.com/profile.php?id=61582004002864",
    icon: Facebook,
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@nurtureskinstudio_",
    icon: () => (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
  },
  {
    name: "WhatsApp",
    href: "https://api.whatsapp.com/send?phone=2347065732481",
    icon: MessageCircle,
  },
];

export function Footer() {
  const { isAdmin } = useAuth();

  return (
    <footer className="bg-gradient-primary border-t border-border" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img
                src={logo}
                alt="Nurture Skin Studio"
                width={64}
                height={64}
                className="h-16 w-16 rounded-full shadow-soft object-cover"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Where luxury meets radiance. Curated skincare products for your unique skin journey.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-card text-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:-translate-y-1 shadow-card"
                  aria-label={social.name}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              {isAdmin && (
                <li>
                  <Link
                    to="/admin/dashboard"
                    className="text-muted-foreground hover:text-primary transition-colors duration-300 text-sm"
                  >
                    Admin Dashboard
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Customer Care */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
              Customer Care
            </h4>
            <ul className="space-y-3">
              {customerCareLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Categories */}
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6 mt-8">
              Shop by Category
            </h4>
            <ul className="space-y-3">
              {categoryLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">
                  Lekki, Lagos, Nigeria
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                <a
                  href="tel:07065732481"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  07065732481
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0" />
                <a
                  href="https://api.whatsapp.com/send?phone=2347065732481"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  Chat on WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Secure Payments */}
        <div className="mt-12 pt-8 border-t border-border/50">
          <SecurePaymentBadge className="mb-8" />
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/50">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm">
              © {new Date().getFullYear()} Nurture Skin Studio. All rights reserved.
            </p>
            <p className="text-muted-foreground text-sm">
              Made by{" "}
              <a
                href="https://api.whatsapp.com/send?phone=2349024673098"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                EDA Media
              </a>
            </p>
            <p className="text-muted-foreground text-sm italic font-serif">
              Where Luxury Meets Radiance
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
